import type { AccountType, BankAccountSummary } from '@/types/accounts';

/** What the global consent-renewal dialog needs to know about the accounts to renew. */
export interface RenewalRequest {
  accountId: string;
  accountIds: string[];
  bankName: string;
  bankBic: string;
  /** A bic-less bank's identity (FIN-885); absent for a bank renewed by its bic. */
  bankRef?: string;
  bankCountryCode?: string;
  accountLabels: string[];
  /** FIN-1567: the stored type of the account being renewed -- see ConsentRefreshDialog's own
   *  doc comment. All of `accounts` share one bank identity already (renewalRequestFor is only
   *  ever called with accounts at the same bank), so the first account's type speaks for all. */
  accountType?: AccountType;
}

export type RenewableAccount = Pick<BankAccountSummary, 'id' | 'bankName' | 'maskedNumber' | 'bic' | 'status' | 'resourceId' | 'providerRef' | 'countryCode' | 'accountType'>;

type ConsentRequiredDetails = {
  bic?: string;
  providerRef?: string;
  countryCode?: string;
  accountsNeedingConsent?: Array<{ accountId?: string; accountName?: string; resourceId?: string }>;
};

/**
 * The accounts a CONSENT_REQUIRED refusal names (payment-api's paySelected, FIN-968), as the
 * account list shows them; null for any other error. An account missing from `accounts` is
 * still named, by its bank alone -- the refusal carries its full IBAN, which is never shown.
 */
export function accessNeededAccountsFrom(error: unknown, accounts: RenewableAccount[]): RenewableAccount[] | null {
  const coded = error as { error?: string; details?: ConsentRequiredDetails } | null | undefined;
  if (coded?.error !== 'CONSENT_REQUIRED') return null;
  const named = coded.details?.accountsNeedingConsent ?? [];
  return named.map((needing) => accounts.find((account) => account.id === needing.accountId) ?? {
    id: needing.accountId ?? '',
    bankName: needing.accountName ?? 'Bank account',
    maskedNumber: null,
    bic: coded.details?.bic ?? '',
    providerRef: coded.details?.providerRef ?? null,
    countryCode: coded.details?.countryCode ?? null,
    status: 'consent_invalid',
    resourceId: needing.resourceId,
  });
}

/** "Nordea ••1234" -- the bank and the server-masked number (FIN-911), the way the Bank page
 *  shows the row. */
export function accountLabel(account: Pick<BankAccountSummary, 'bankName' | 'maskedNumber'>): string {
  return account.maskedNumber ? `${account.bankName} ${account.maskedNumber}` : account.bankName;
}

export function renewalRequestFor(accounts: RenewableAccount[]): RenewalRequest {
  const [first] = accounts;
  // The renewal reconnects each account by the provider's own id (connectAccount), which also
  // handles moving it to a different provider; the row's own id is only a fallback.
  const renewalIds = accounts.map((account) => account.resourceId ?? account.id);
  return {
    accountId: renewalIds[0],
    accountIds: renewalIds,
    bankName: first.bankName,
    bankBic: first.bic,
    ...(first.providerRef ? { bankRef: first.providerRef } : {}),
    ...(first.countryCode ? { bankCountryCode: first.countryCode } : {}),
    ...(first.accountType ? { accountType: first.accountType } : {}),
    accountLabels: accounts.map(accountLabel),
  };
}

/**
 * The account list returns an account whose consent is gone as a `consent_invalid` row rather
 * than throwing (FIN-863). When every account is in that state there is nothing else to show,
 * so the renewal dialog opens by itself; when only some are, each row offers Renew access.
 */
export function renewalRequestIfAllAccessLost(accounts: RenewableAccount[]): RenewalRequest | null {
  if (accounts.length === 0 || !accounts.every((account) => account.status === 'consent_invalid')) return null;
  return renewalRequestFor(accounts);
}

/** The Linear issue tracking the unreachable "bank stored without its identity" case. */
const RENEWAL_COUNTRY_MISSING_TRACKING_ISSUE = 'FIN-1060';

/**
 * The error reported when a renewal is refused for that case. Its payload rides on
 * `requestMetadata`, the same field name backend diagnostics use, and the tracking issue is in
 * the message too so it shows in Sentry's title and the console line.
 */
export function renewalRefusedError(bankRef?: string): Error & { requestMetadata: Record<string, string | null> } {
  return Object.assign(
    new Error(`BANK_RENEWAL_COUNTRY_MISSING (${RENEWAL_COUNTRY_MISSING_TRACKING_ISSUE}): renewal requested for a bank with no bic and no ref+country`),
    { requestMetadata: { errorType: 'BANK_RENEWAL_COUNTRY_MISSING', trackingIssue: RENEWAL_COUNTRY_MISSING_TRACKING_ISSUE, bankRef: bankRef ?? null } },
  );
}

/**
 * How a renewal can identify the bank: by its bic, or by its stored ref and country (a bank with
 * no bic, FIN-885). Anything else is `unrenewable` -- refused, never guessed.
 */
export function renewalIdentity(bankBic?: string, bankRef?: string, bankCountryCode?: string): 'bic' | 'ref' | 'unrenewable' {
  if (bankBic) return 'bic';
  if (bankRef && bankCountryCode) return 'ref';
  return 'unrenewable';
}
